<template>
    <generic-task-execution-modal
        :asset="asset"
        :assignable-users="assignableUsers"
        :description="description"
        :form="form"
        :task-resolved="taskResolved"
        :primary-button-label="primaryButtonLabel"
        :editable="editable"
        :task-code="taskCode"
        :task-group="taskGroup"
        :task-id="taskId"
        :title="title"
        @submit="handleSubmit"
    >
        <form-group
            :horizontal="{ label: 'col-md-3', input: 'col-md-9' }"
            :label="trans('check_asset_status.modal.operability.label')"
            group-classes="mb-9"
        >
            <asset-operability-switch id="status-check-operability-switch" v-model="form.operability" :disabled="!editable" />
        </form-group>
        <form-group
            :horizontal="{ label: 'col-md-3', input: 'col-md-9' }"
            :label="trans('check_asset_status.modal.usage_category.label')"
            group-classes="mb-9"
        >
            <usage-category-selection-dropdown
                v-model="form.usageCategory"
                :disabled="!editable || form.processing"
                :usage-categories="usageCategories.data || []"
            />
        </form-group>

        <form-group v-if="form.operability === false" :horizontal="false" :label="trans('check_asset_status.modal.reason_for_status_change.label')">
            <input-radio-group v-model="form.reason" :disabled="!editable" :invalidFeedback="form.errors.reason" :options="reasons.data || []" />
        </form-group>

        <row v-if="statusHasChanged" class="mb-8">
            <column>
                <alert class="badge-warning mb-0" color="warning">
                    <div
                        v-html="
                            trans('check_asset_status.modal.content.confirm_change', {
                                status: statusName,
                            })
                        "
                    />
                </alert>
            </column>
        </row>
    </generic-task-execution-modal>
</template>
<script setup>
import { eventBus, events } from '@/eventBus';
import AssetOperabilitySwitch from '@/Pages/Assets/Details/AssetOperabilitySwitch.vue';
import UsageCategorySelectionDropdown from '@/Pages/Assets/Details/UsageCategorySelectionDropdown.vue';
import InputRadioGroup from '@/Components/InputRadioGroup.vue';
import FormGroup from '@/Components/FormGroup.vue';
import { computed, defineAsyncComponent } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n';
const GenericTaskExecutionModal = defineAsyncComponent(() => import('@/Pages/Modals/GenericTaskExecutionModal.vue'));
import Alert from '@/Components/Alert.vue';

const props = defineProps({
    asset: {
        type: Object,
        required: true,
    },
    statusCheck: Object,
    usageCategories: {
        type: Object,
        required: true,
    },
    reasons: {
        type: Object,
        required: true,
    },
    assignableUsers: Object,
    currentUserId: String,
    taskId: String,
    taskCode: String,
    taskType: String,
    taskGroup: String,
    assetId: String,
    title: String,
    description: String,
    editable: {
        type: Boolean,
        default: true,
    },
    links: Object,
    existingPerformedById: String,
    taskResolved: { type: Boolean, default: false },
});

const initialOperabilityValue = computed(() => (props.statusCheck.operability || props.asset.status.operability) === 'operable');

const form = useForm({
    operability: initialOperabilityValue.value,
    usageCategory: props.statusCheck.usageCategory || props.asset.status.usageCategory,
    reason: props.statusCheck.reason || 'corrective_maintenance_required',
    description: props.statusCheck.description,
    taskId: props.taskId,
    task_type: props.taskType,
    asset_id: props.assetId,
    performedById: props.existingPerformedById ?? props.currentUserId,
    attachments: [],
});

const currentAssetUsageCategory = computed(() => props.usageCategories.data.find((item) => item.value === form.usageCategory));
const currentOperability = computed(() => (form.operability ? 'operable' : 'inoperable'));
// Needed to determine whether to show the optional section "reason for change"
const hasOperabilityChanged = computed(() => initialOperabilityValue.value !== form.operability);
const hasUsageCategoryChanged = computed(() => props.asset.status.usageCategory !== form.usageCategory);

const statusName = computed(() => {
    const operability = trans(`general.assetstatus.${currentOperability.value}`);
    const usageCategory = trans(`assets.status.${currentAssetUsageCategory?.value.value}`);
    return `${operability}/${usageCategory}`;
});
const statusHasChanged = computed(() => form.isDirty && (hasOperabilityChanged.value || hasUsageCategoryChanged.value));
const primaryButtonLabel = computed(() => {
    if (props.taskResolved) {
        return trans('tasks.modal.awaiting_approval.save_changes');
    }
    return statusHasChanged.value ? trans('general.action.update') : trans('general.action.confirm');
});

const handleSubmit = (modal) => {
    if (form.processing) {
        return;
    }

    form.transform(transformData).post(props.links.create, {
        preserveScroll: true,
        onSuccess: () => {
            eventBus.$emit(events.updateNotifications);

            if (hasOperabilityChanged) {
                eventBus.$emit(events.assetOperabilityUpdated, {
                    asset: props.asset.id,
                    ...transformData(form.data()),
                });
            }

            modal.close();
        },
    });
};

const transformData = (data) => {
    data.operability = data.operability ? 'operable' : 'inoperable';

    if (!hasOperabilityChanged || data.operability === 'operable') {
        delete data.reason;
    }

    return data;
};
</script>
